/**
 * Which meeting reminders this tab has already shown, so a reminder appears once however it
 * arrives: the server's in-room `ROOM_BOOKING_REMINDER` while the user is in the game, or the
 * dashboard's own timer (`useDashboardBookingReminders`) while they are in the main menu. Without
 * a shared record, leaving the room after the in-room reminder fired the dashboard one again.
 *
 * A reminder is its meeting's start time and `remindBefore`, not just the meeting: moving either
 * re-arms the reminder email (cloud `resolveReminderSent`), so the in-app reminder shows again
 * too, while a title-only edit does not.
 *
 * Kept in sessionStorage (per tab, survives a refresh, no network). The key predates the in-room
 * check and is kept so tabs open across a deploy keep what they have already shown; their older
 * entries (bare booking ids) simply no longer match.
 */
const STORAGE_KEY = 'dashboard_shown_reminders'

export interface MeetingReminderRef {
  bookingId: string
  startTime: string | number | Date
  /** Minutes before the start. */
  remindBefore: number
}

function reminderKey(r: MeetingReminderRef): string {
  const ms = new Date(r.startTime).getTime()
  return `${r.bookingId}|${Number.isFinite(ms) ? ms : String(r.startTime)}|${r.remindBefore}`
}

function readShown(): Set<string> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    const ids = raw ? JSON.parse(raw) : []
    return new Set(Array.isArray(ids) ? ids.filter((id) => typeof id === 'string') : [])
  } catch {
    return new Set()
  }
}

export function hasShownMeetingReminder(reminder: MeetingReminderRef): boolean {
  return readShown().has(reminderKey(reminder))
}

/**
 * Records the reminder as shown and returns true, or returns false when it was already shown, so
 * the caller shows it only on true.
 */
export function claimMeetingReminder(reminder: MeetingReminderRef): boolean {
  const shown = readShown()
  const key = reminderKey(reminder)
  if (shown.has(key)) return false
  shown.add(key)
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...shown]))
  } catch {
    // Storage full or blocked: still show it once now
  }
  return true
}

/** "Starts in 1 minute" / "Starts in 5 minutes". */
export function startsInText(minutes: number): string {
  const n = Math.max(1, Math.round(minutes))
  return `Starts in ${n} ${n === 1 ? 'minute' : 'minutes'}`
}
